// "Command Center" dashboard: KPI tiles, Live Fleet table, Fleet Health donut,
// Quick Actions grid, Recent Activity. Data: GET /api/portal/dashboard/stats,
// GET /api/portal/robots, plus the fleet socket room.
import { useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { getSocket } from "../../services/socketService";
import { apiFetch } from "../../services/http";
import CONFIG from "../../config/config";
import { battAriaLabel } from "../../utils/battery";
import {
  Card,
  SectionHead,
  Chip,
  Btn,
  Icon,
  Avatar,
  BatteryBar,
  Donut,
  Legend,
  pageContainerSx,
} from "../../components/ui";

const KPI_SKELETON_KEYS = ["fleet-total", "active", "inactive", "error", "charging", "ads"];

const STATUS_TONES = { active: "success", error: "danger" };
const statusTone = (status) => STATUS_TONES[status] || "warning";

const initials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("") || "?";

const lowBatterySummary = (count) => {
  if (count === 0) return "All robots charged or charging";
  const plural = count === 1 ? "" : "s";
  const verb = count === 1 ? "s" : "";
  return `${count} robot${plural} need${verb} attention`;
};

const timeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
};

const NOTIFICATION_META = {
  robot_assigned: { icon: "bot", tone: "accent" },
  robot_removed: { icon: "bot", tone: "warning" },
  advertisement_approved: { icon: "badge-check", tone: "success" },
  advertisement_rejected: { icon: "circle-x", tone: "danger" },
  advertisement_expired: { icon: "clock", tone: "warning" },
  registration_approved: { icon: "user-check", tone: "success" },
  registration_rejected: { icon: "user-x", tone: "danger" },
  general: { icon: "info", tone: "accent" },
};

const TONE_COLORS = {
  success: "var(--bq-success)",
  warning: "var(--bq-warning)",
  danger: "var(--bq-danger)",
  accent: "var(--bq-accent)",
};
const toneColor = (t) => TONE_COLORS[t] || TONE_COLORS.accent;

const KPI_TONES = {
  accent: { fg: "var(--bq-accent)", soft: "var(--bq-accentSoft)" },
  success: { fg: "var(--bq-success)", soft: "var(--bq-successSoft)" },
  warning: { fg: "var(--bq-warning)", soft: "var(--bq-warningSoft)" },
  danger: { fg: "var(--bq-danger)", soft: "var(--bq-dangerSoft)" },
};

/* ── KPI tile: tinted icon chip, tone-coloured number, top accent rule ────── */
const Kpi = ({ k, onClick }) => {
  const tone = KPI_TONES[k.tone] || KPI_TONES.accent;
  return (
    <button
      onClick={onClick}
      className="rp-hover-lift rp-kpi"
      style={{
        position: "relative",
        overflow: "hidden",
        textAlign: "left",
        background: "var(--bq-surface)",
        border: "1px solid var(--bq-border)",
        borderRadius: 14,
        padding: "15px 16px 14px",
        display: "flex",
        flexDirection: "column",
        gap: 12,
        cursor: "pointer",
      }}
    >
      <span
        aria-hidden="true"
        style={{ position: "absolute", inset: "0 0 auto 0", height: 3, background: tone.fg }}
      />
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 10,
            flex: "none",
            display: "grid",
            placeItems: "center",
            background: tone.soft,
            color: tone.fg,
          }}
        >
          <Icon name={k.icon} size={19} />
        </div>
        <span
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: "var(--bq-text)",
            letterSpacing: "-.01em",
          }}
        >
          {k.label}
        </span>
      </div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 9, flexWrap: "wrap" }}>
        <span
          style={{
            fontSize: 34,
            fontWeight: 800,
            lineHeight: 1,
            letterSpacing: "-.035em",
            color: tone.fg,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {k.value}
        </span>
        {k.hint && (
          <span style={{ fontSize: 11.5, fontWeight: 500, color: "var(--bq-text3)" }}>
            {k.hint}
          </span>
        )}
      </div>
    </button>
  );
};
Kpi.propTypes = { k: PropTypes.object.isRequired, onClick: PropTypes.func };

const KpiRow = ({ loading, kpis, onNavigate }) => {
  if (loading) {
    return KPI_SKELETON_KEYS.map((key) => (
      <div
        key={`kpi-skeleton-${key}`}
        style={{
          height: 112,
          borderRadius: 14,
          background: "var(--bq-surface2)",
          border: "1px solid var(--bq-border)",
        }}
      />
    ));
  }
  return kpis.map((k) => <Kpi key={k.label} k={k} onClick={() => onNavigate(k.route)} />);
};
KpiRow.propTypes = {
  loading: PropTypes.bool,
  kpis: PropTypes.array.isRequired,
  onNavigate: PropTypes.func.isRequired,
};

/* ── Live fleet row ────────────────────────────────────────────────────────── */
const FleetRow = ({ robot, battery, isCharging, isLast, onClick }) => (
  <button
    onClick={onClick}
    className="rp-hover-surface rp-fleet-row"
    style={{
      display: "grid",
      gridTemplateColumns: "minmax(0,1.6fr) minmax(0,1.1fr) 90px 70px",
      gap: 10,
      alignItems: "center",
      width: "100%",
      textAlign: "left",
      padding: "9px 14px",
      borderBottom: isLast ? "none" : "1px solid var(--bq-border)",
      background: "transparent",
      border: "none",
      cursor: "pointer",
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: 9, minWidth: 0 }}>
      <Avatar code={initials(robot.name)} />
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {robot.name}
        </div>
        <div
          style={{
            fontSize: 11,
            color: "var(--bq-text3)",
            fontFamily: "'JetBrains Mono', monospace",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {robot.serialID}
          {robot.type || robot.roverType ? ` · ${robot.type || robot.roverType}` : ""}
        </div>
      </div>
    </div>
    <div
      className="rp-fleet-map"
      style={{
        fontSize: 12,
        color: "var(--bq-text2)",
        display: "flex",
        alignItems: "center",
        gap: 6,
        minWidth: 0,
      }}
    >
      <Icon name="map-pin" size={12} color="var(--bq-text3)" />
      <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        {robot.assignedMap?.name || "Not assigned"}
      </span>
    </div>
    {typeof battery === "number" ? (
      <div title={battAriaLabel(battery, isCharging)}>
        {/* The bar carries level and condition in colour alone, which neither a
            screen reader nor a colour-blind user can read. Real hidden text
            rather than an ARIA role: the bar is CSS, not an image. */}
        <span className="bq-sr-only">{battAriaLabel(battery, isCharging)}</span>
        <BatteryBar value={battery} width={40} charging={isCharging} />
      </div>
    ) : (
      <span style={{ fontSize: 11, color: "var(--bq-text3)" }}>—</span>
    )}
    <div style={{ display: "flex", justifyContent: "flex-end" }}>
      <Chip label={robot.status} tone={statusTone(robot.status)} />
    </div>
  </button>
);
FleetRow.propTypes = {
  robot: PropTypes.object.isRequired,
  battery: PropTypes.number,
  isCharging: PropTypes.bool,
  isLast: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
};

/* ── Recent activity row ───────────────────────────────────────────────────── */
const ActivityItem = ({ activity }) => {
  const meta = NOTIFICATION_META[activity.type] || NOTIFICATION_META.general;
  return (
    <div style={{ display: "flex", gap: 11, padding: "9px 14px" }}>
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: 8,
          flex: "none",
          display: "grid",
          placeItems: "center",
          background: "var(--bq-surface3)",
          color: toneColor(meta.tone),
        }}
      >
        <Icon name={meta.icon} size={15} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12.5, color: "var(--bq-text)" }}>{activity.title}</div>
        <div style={{ fontSize: 11, color: "var(--bq-text3)", marginTop: 1 }}>
          {activity.message}
        </div>
      </div>
      <span style={{ fontSize: 11, color: "var(--bq-text3)", flex: "none" }}>
        {timeAgo(activity.createdAt)} ago
      </span>
    </div>
  );
};
ActivityItem.propTypes = { activity: PropTypes.object.isRequired };

const Dashboard = () => {
  const navigate = useNavigate();

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [robots, setRobots] = useState([]);
  const [battBySerial, setBattBySerial] = useState({});
  const [connBySerial, setConnBySerial] = useState({});

  useEffect(() => {
    const token = localStorage.getItem("portalToken");
    const auth = { headers: { Authorization: `Bearer ${token}` } };

    const fetchStats = async () => {
      try {
        const res = await apiFetch(`${CONFIG.BASE_URL}/api/portal/dashboard/stats`, auth);
        if (res.ok) setStats(await res.json());
      } catch {
        /* silent */
      } finally {
        setLoading(false);
      }
    };
    const fetchRobots = async () => {
      try {
        const res = await apiFetch(`${CONFIG.BASE_URL}/api/portal/robots`, auth);
        if (res.ok) setRobots(await res.json());
      } catch {
        /* silent */
      }
    };
    fetchStats();
    fetchRobots();
  }, []);

  // Live battery + connectivity, same socket contract as MyRobots and
  // botriq-admin-fe's Dashboard.
  useEffect(() => {
    const socket = getSocket();
    const onBattery = (msg) => {
      const serial = msg?.robotId ?? msg?.data?.serialID;
      const level = msg?.data?.batteryLevel;
      if (serial != null && typeof level === "number") {
        setBattBySerial((prev) => ({ ...prev, [serial]: { ...prev[serial], level } }));
      }
    };
    const onSensorStatus = (msg) => {
      const serial = msg?.serialID;
      const fraction = msg?.battery;
      const charging = msg?.charging;
      if (serial == null) return;
      setBattBySerial((prev) => ({
        ...prev,
        [serial]: {
          ...prev[serial],
          ...(typeof fraction === "number" ? { level: fraction * 100 } : {}),
          ...(typeof charging === "boolean" ? { charging } : {}),
        },
      }));
    };
    const markConnected = (msg) => {
      const serial = msg?.serialID ?? msg?.robotId ?? msg?.data?.serialID;
      if (serial == null) return;
      const key = String(serial);
      setConnBySerial((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
    };
    const markDisconnected = (msg) => {
      const serial = msg?.serialID ?? msg?.robotId ?? msg?.data?.serialID;
      if (serial == null) return;
      setConnBySerial((prev) => ({ ...prev, [String(serial)]: false }));
    };

    const joinFleet = () => socket.emit("subscribe-fleet");
    socket.on("connect", joinFleet);
    if (socket.connected) joinFleet();

    socket.on("BATTERY_STATUS_UPDATE", onBattery);
    socket.on("BATTERY_STATUS_UPDATE", markConnected);
    socket.on("SENSOR_STATUS_UPDATE", onSensorStatus);
    socket.on("SENSOR_STATUS_UPDATE", markConnected);
    socket.on("ACTION_CURRENT_POSE", markConnected);
    socket.on("nav_progress", markConnected);
    socket.on("location_data", markConnected);
    socket.on("robot-status", markConnected);
    socket.on("robot_connected", markConnected);
    socket.on("robot_disconnected", markDisconnected);
    return () => {
      socket.off("connect", joinFleet);
      socket.off("BATTERY_STATUS_UPDATE", onBattery);
      socket.off("BATTERY_STATUS_UPDATE", markConnected);
      socket.off("SENSOR_STATUS_UPDATE", onSensorStatus);
      socket.off("SENSOR_STATUS_UPDATE", markConnected);
      socket.off("ACTION_CURRENT_POSE", markConnected);
      socket.off("nav_progress", markConnected);
      socket.off("location_data", markConnected);
      socket.off("robot-status", markConnected);
      socket.off("robot_connected", markConnected);
      socket.off("robot_disconnected", markDisconnected);
    };
  }, []);

  const totalAds = stats?.advertisements?.total ?? 0;

  // A robot counts as active when stored active OR currently reporting live over
  // the socket. Mirrors resolveRobotStatus in MyRobots and botriq-admin-fe's Dashboard.
  const isRobotActive = (r) =>
    r.status === "active" ||
    (r.status !== "error" && r.status !== "unassigned" && !!connBySerial[String(r.serialID)]);

  const fleetList = useMemo(
    () =>
      robots.map((r) =>
        isRobotActive(r) && r.status !== "active" ? { ...r, status: "active" } : r
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [robots, connBySerial]
  );

  const totalRobots = robots.length;
  const activeRobots = fleetList.filter((r) => r.status === "active").length;
  const inactiveRobots = Math.max(0, totalRobots - activeRobots);
  const errorRobots = robots.filter((r) => r.status === "error").length;
  const chargingRobots = robots.filter((r) => r.charging).length;
  const idleRobots = Math.max(0, totalRobots - activeRobots - errorRobots);

  const kpis = useMemo(
    () => [
      {
        label: "Fleet total",
        value: String(totalRobots),
        icon: "bot",
        tone: "accent",
        hint: "All robots",
        route: "/robots",
      },
      {
        label: "Active",
        value: String(activeRobots),
        icon: "radio-tower",
        tone: "success",
        hint: "Online now",
        route: "/robots",
      },
      {
        label: "Inactive",
        value: String(inactiveRobots),
        icon: "pause-circle",
        tone: "warning",
        hint: "Awaiting work",
        route: "/robots",
      },
      {
        label: "Error",
        value: String(errorRobots),
        icon: "triangle-alert",
        tone: "danger",
        hint: "Needs attention",
        route: "/robots",
      },
      {
        label: "Charging",
        value: String(chargingRobots),
        icon: "battery-charging",
        tone: "warning",
        hint: "On the dock",
        route: "/robots",
      },
      {
        label: "Total ads",
        value: String(totalAds),
        icon: "megaphone",
        tone: "accent",
        hint: "Campaigns",
        route: "/advertisements",
      },
    ],
    [totalRobots, activeRobots, inactiveRobots, errorRobots, chargingRobots, totalAds]
  );

  const recentActivity = stats?.recentActivity || [];

  // Dashboard preview: the robots that most need a look, not the whole roster
  // (that's what "View all" -> /robots is for). Charging robots are already
  // being addressed, so they're excluded even if their level is currently
  // low — this list is for batteries draining unattended.
  const lowBatteryFleet = useMemo(() => {
    const withLevel = fleetList
      .map((r) => {
        const battEntry = r.serialID != null ? battBySerial[r.serialID] : null;
        const level = battEntry?.level ?? null;
        const isCharging = battEntry?.charging ?? !!r.charging;
        return { robot: r, level, isCharging };
      })
      .filter((entry) => typeof entry.level === "number" && !entry.isCharging);
    withLevel.sort((a, b) => a.level - b.level);
    return withLevel.slice(0, 3);
  }, [fleetList, battBySerial]);

  return (
    <Box sx={{ ...pageContainerSx(), pb: { xs: 4, sm: 5 }, animation: "rp-in .18s ease" }}>
      {/* Page head */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          gap: 14,
          marginBottom: 16,
          flexWrap: "wrap",
        }}
      >
        <div>
          <h1
            style={{
              margin: 0,
              fontSize: 20,
              fontWeight: 700,
              letterSpacing: "-.02em",
              color: "var(--bq-text)",
            }}
          >
            Dashboard
          </h1>
        </div>
      </div>

      {/* KPI row */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(178px,1fr))",
          gap: 12,
          marginBottom: 16,
        }}
      >
        <KpiRow loading={loading} kpis={kpis} onNavigate={navigate} />
      </div>

      {/* Main grid */}
      <div
        className="rp-dash-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0,2fr) minmax(280px,1fr)",
          gap: 14,
          alignItems: "start",
        }}
      >
        {/* LEFT */}
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Card>
            <SectionHead
              icon="battery-warning"
              iconTone="warning"
              title="Low Battery"
              sub={lowBatterySummary(lowBatteryFleet.length)}
              right={
                <Btn
                  label="View all"
                  icon="arrow-right"
                  tone="subtle"
                  size="sm"
                  onClick={() => navigate("/robots")}
                />
              }
            />
            <div>
              {lowBatteryFleet.length === 0 && !loading && (
                <div
                  style={{
                    padding: "24px 14px",
                    textAlign: "center",
                    fontSize: 13,
                    color: "var(--bq-text3)",
                  }}
                >
                  No robots currently low on battery.
                </div>
              )}
              {lowBatteryFleet.map(({ robot: r, level, isCharging }, i) => (
                <FleetRow
                  key={r._id}
                  robot={r}
                  battery={level}
                  isCharging={isCharging}
                  isLast={i === lowBatteryFleet.length - 1}
                  onClick={() => navigate(`/robots/${r._id}`)}
                />
              ))}
            </div>
          </Card>

          <Card>
            <SectionHead
              icon="history"
              title="Recent Activity"
              right={
                <Btn
                  label="View log"
                  icon="arrow-right"
                  tone="subtle"
                  size="sm"
                  onClick={() => navigate("/notifications")}
                />
              }
            />
            <div style={{ padding: "4px 0" }}>
              {recentActivity.length === 0 && !loading && (
                <div
                  style={{
                    padding: "20px 14px",
                    textAlign: "center",
                    fontSize: 13,
                    color: "var(--bq-text3)",
                  }}
                >
                  No recent activity.
                </div>
              )}
              {recentActivity.map((a) => (
                <ActivityItem key={a._id} activity={a} />
              ))}
            </div>
          </Card>
        </div>

        {/* RIGHT */}
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Card>
            <SectionHead icon="heart-pulse" iconTone="success" title="Fleet Health" />
            <div style={{ padding: 14, display: "flex", alignItems: "center", gap: 16 }}>
              <Donut
                segs={[
                  [activeRobots, "var(--bq-success)"],
                  [idleRobots, "var(--bq-warning)"],
                  [errorRobots, "var(--bq-danger)"],
                ]}
                size={96}
              />
              <div style={{ display: "flex", flexDirection: "column", gap: 8, flex: 1 }}>
                <Legend color="var(--bq-success)" label="Active" value={activeRobots} />
                <Legend color="var(--bq-warning)" label="Idle" value={idleRobots} />
                <Legend color="var(--bq-danger)" label="Errors" value={errorRobots} />
              </div>
            </div>
            <div
              style={{
                padding: "0 14px 14px",
                borderTop: "1px solid var(--bq-border)",
                marginTop: 2,
                paddingTop: 12,
              }}
            >
              <Legend color="var(--bq-warning)" label="Charging" value={chargingRobots} />
            </div>
          </Card>

          <Card>
            <SectionHead icon="zap" iconTone="warning" title="Quick Actions" />
            <div style={{ padding: 12, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 9 }}>
              {[
                { icon: "bot", label: "My Robots", tone: "accent", route: "/robots" },
                {
                  icon: "megaphone",
                  label: "Advertisements",
                  tone: "success",
                  route: "/advertisements",
                },
                {
                  icon: "bell-ring",
                  label: "Notifications",
                  tone: "warning",
                  route: "/notifications",
                },
                { icon: "building-2", label: "Company Profile", tone: "accent", route: "/profile" },
              ].map((a) => {
                const [soft, fg] = {
                  accent: ["var(--bq-accentSoft)", "var(--bq-accent)"],
                  success: ["var(--bq-successSoft)", "var(--bq-success)"],
                  warning: ["var(--bq-warningSoft)", "var(--bq-warning)"],
                }[a.tone];
                return (
                  <button
                    key={a.route}
                    onClick={() => navigate(a.route)}
                    className="rp-quick"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      gap: 10,
                      padding: "12px 12px 13px",
                      borderRadius: 11,
                      border: "1px solid var(--bq-border)",
                      background: "var(--bq-surface2)",
                      color: "var(--bq-text)",
                      cursor: "pointer",
                      transition: "border-color .13s, background .13s",
                    }}
                  >
                    <span
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 9,
                        display: "grid",
                        placeItems: "center",
                        background: soft,
                        color: fg,
                      }}
                    >
                      <Icon name={a.icon} size={17} />
                    </span>
                    <span style={{ fontSize: 12.5, fontWeight: 600 }}>{a.label}</span>
                  </button>
                );
              })}
            </div>
          </Card>
        </div>
      </div>

      <style>{`
        .rp-quick:hover { border-color: var(--bq-accent) !important; background: var(--bq-accentSoft) !important; }
        .rp-kpi:focus-visible { outline: 2px solid var(--bq-accent); outline-offset: 2px; }
        .rp-quick:focus-visible { outline: 2px solid var(--bq-accent); outline-offset: 2px; }
        @media (max-width: 1000px) { .rp-dash-grid { grid-template-columns: 1fr !important; } }
        @media (max-width: 560px) {
          .rp-fleet-row { grid-template-columns: minmax(0,1fr) 62px 70px !important; }
          .rp-fleet-map { display: none !important; }
        }
      `}</style>
    </Box>
  );
};

export default Dashboard;
